/**
 * TurnSimulationPanel - Turn stepping controls for a kept sample hand
 *
 * Owns nothing: the run lives in `useTurnSimulation` so the sample hand can
 * reset it whenever a new hand is dealt, undone or restored.
 */
import type { ReactElement } from 'react';
import { IoPlayForward, IoPlaySkipForward } from 'react-icons/io5';

import type { TurnSimulationState } from '@/features/decks/hooks/useTurnSimulation';
import type { SimCard, TurnSimOptions } from '@/features/decks/lib/handSim';

import TurnTimeline from './TurnTimeline';

// ============================================================================
// Types
// ============================================================================

export interface TurnSimulationPanelProps {
  /** The kept hand the run starts from */
  hand: SimCard[];
  options: TurnSimOptions;
  state: TurnSimulationState;
  onOpenDetails: (card: SimCard) => void;
}

// ============================================================================
// Component
// ============================================================================

export default function TurnSimulationPanel({ hand, options, state, onOpenDetails }: TurnSimulationPanelProps): ReactElement {
  const { run, turnsSimulated, atLimit } = state;

  return (
    <div className="space-y-2 rounded-lg bg-zinc-800/30 p-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-ash text-[10px] tracking-wider uppercase">
          Turn simulation · {options.onThePlay ? 'on the play' : 'on the draw'}
        </span>
        {run && (
          <span className="text-ash/70 text-[10px] tabular-nums">
            T{run.revealed}/{turnsSimulated}
          </span>
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={state.next}
          disabled={atLimit}
          className="bg-aether hover:bg-aether/80 flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-500"
        >
          <IoPlaySkipForward className="h-3.5 w-3.5" />
          Next Turn
        </button>
        <button
          type="button"
          onClick={state.playAll}
          disabled={atLimit}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-800 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none disabled:cursor-not-allowed disabled:border-zinc-800 disabled:text-zinc-600"
        >
          <IoPlayForward className="h-3.5 w-3.5" />
          Play all {options.turns} turns
        </button>
      </div>

      {run ? (
        <TurnTimeline hand={hand} simulation={run.simulation} revealed={run.revealed} onOpenDetails={onOpenDetails} />
      ) : (
        <p className="text-ash/70 text-[11px]">
          Step this hand forward to see where it stalls. Draws, land drops and casts only — no ramp, tutors or card draw.
        </p>
      )}

      {atLimit && (
        <p className="text-ash/70 text-[11px]" role="status">
          {run?.simulation.decked
            ? 'The library ran out — nothing left to draw.'
            : `Reached turn ${turnsSimulated}. Raise "Turns to simulate" in Simulation settings to go further.`}
        </p>
      )}
    </div>
  );
}
